import { useTranslation } from 'react-i18next';
import { getFurnitureThumbPath, getFurnitureName } from '../../../data/furniture';
import type { FurnitureCatalog } from '../../../models/apiTypes';

const STAR_ICON = '/ui/telegram-stars-icon.png';
const SHELL_ICON = '/ui/shell-icon.png';

interface PreviewPurchaseModalProps {
  furniture: FurnitureCatalog;
  loading: 'pearls' | 'stars' | null;
  onBuy: () => void | Promise<void>;
  onDismiss: () => void;
}

/** Preview purchase modal — buy the currently test-driven furniture item. */
export default function PreviewPurchaseModal({ furniture, loading, onBuy, onDismiss }: PreviewPurchaseModalProps) {
  const { t } = useTranslation();

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 3500,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        }}
        onPointerDown={onDismiss}
      />
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 3501,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          padding: 20,
          boxSizing: 'border-box',
        }}
      >
        <article
          style={{
            borderRadius: 20,
            padding: '13px 20px',
            border: '2px solid rgba(55, 191, 164, 0.5)',
            background: 'linear-gradient(135deg, rgba(55, 191, 164, 0.18) 0%, rgba(55, 191, 164, 0.06) 100%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            width: '100%',
            maxWidth: 360,
            boxSizing: 'border-box',
            pointerEvents: 'auto',
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
            <div style={{ position: 'relative', width: 143, height: 143, flexShrink: 0 }}>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: 143,
                  height: 143,
                  aspectRatio: '1/1',
                  background: 'radial-gradient(50% 50% at 50% 50%, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)',
                  filter: 'blur(11.75px)',
                  borderRadius: 18,
                }}
              />
              <img
                src={getFurnitureThumbPath(furniture)}
                alt={getFurnitureName(furniture.id, t)}
                style={{
                  position: 'relative',
                  width: 111.231,
                  height: 111.231,
                  borderRadius: 18,
                  objectFit: 'contain',
                  margin: 15.89,
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, maxWidth: '100%' }}>
              <p
                style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: '29px',
                  letterSpacing: '-0.747px',
                  color: '#fff',
                  margin: 0,
                  textAlign: 'center',
                }}
              >
                {t('Buy now')}
              </p>

              {(() => {
                const price = furniture.price ?? null;
                const currency = furniture.currency ?? null;
                const isFree = price === null || price === 0;
                return (
                  <button
                    type="button"
                    onClick={onBuy}
                    disabled={loading !== null}
                    style={{
                      borderRadius: 100,
                      background: '#FFF',
                      color: '#333',
                      fontFamily: 'Rubik One, sans-serif',
                      fontSize: 16,
                      fontWeight: 400,
                      lineHeight: '29px',
                      letterSpacing: '-0.747px',
                      padding: '6px 16px',
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 6,
                      border: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1,
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box',
                    }}
                  >
                    {isFree ? t('Get for free') : (
                      <>
                        {price}
                        {currency === 'stars'
                          ? <img src={STAR_ICON} alt={t('Star')} draggable={false} style={{ width: 19, height: 20, flexShrink: 0 }} />
                          : <img src={SHELL_ICON} alt={t('Shell')} draggable={false} style={{ width: 32.02, height: 25.33, flexShrink: 0 }} />
                        }
                      </>
                    )}
                  </button>
                );
              })()}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h3
              style={{
                fontFamily: 'Rubik One, sans-serif',
                fontSize: 18,
                fontWeight: 400,
                lineHeight: '30px',
                color: '#fff',
                margin: 0,
              }}
            >
              {getFurnitureName(furniture.id, t)}
            </h3>
          </div>
        </article>
      </div>
    </>
  );
}
